import links from './links';

export type SearchResultType = 'OBJECTIVE' | 'PROJECT' | 'TASK';

export type SearchResult = {
  id: string;
  name: string;
  slug?: string;
  type: SearchResultType;
};

const TYPE_ORDER: Record<SearchResultType, number> = {
  PROJECT: 0,
  OBJECTIVE: 1,
  TASK: 2,
};

const isWordBoundary = (text: string, index: number) => (
  index === 0 || text[index - 1] === ' '
);

export const fuzzyScore = (value: string, query: string): number | null => {
  const text = value.toLowerCase();
  let score = 0;
  let consecutive = 0;
  let textIndex = 0;

  for (let queryIndex = 0; queryIndex < query.length; queryIndex += 1) {
    const matchIndex = text.indexOf(query[queryIndex], textIndex);
    if (matchIndex === -1) {
      return null;
    }

    if (matchIndex === textIndex && queryIndex > 0) {
      consecutive += 1;
    } else {
      consecutive = 0;
    }

    if (isWordBoundary(text, matchIndex)) {
      score += 8;
    }
    score += 1 + consecutive * 4;
    textIndex = matchIndex + 1;
  }

  return score / text.length;
};

export const getSearchResults = (
  query: string,
  sources: {
    objectives?: Array<{ id: string; name: string; slug?: string }> | null;
    projects?: Array<{ id: string; name: string; slug?: string }> | null;
    tasks?: Array<{ description: string; id?: string; slug?: string }> | null;
  } = {},
): SearchResult[] => {
  const inputValue = query.trim().toLowerCase();

  if (!inputValue) {
    return [];
  }

  const candidates: Array<{ result: SearchResult; score: number }> = [
    ...(sources.projects ?? []).map((project): SearchResult => ({
      id: project.id,
      name: project.name,
      ...(project.slug ? { slug: project.slug } : {}),
      type: 'PROJECT',
    })),
    ...(sources.objectives ?? []).map((objective): SearchResult => ({
      id: objective.id,
      name: objective.name,
      ...(objective.slug ? { slug: objective.slug } : {}),
      type: 'OBJECTIVE',
    })),
    ...(sources.tasks ?? [])
      .filter(task => Boolean(task.id))
      .map((task): SearchResult => ({
        id: task.id as string,
        name: task.description,
        ...(task.slug ? { slug: task.slug } : {}),
        type: 'TASK',
      })),
  ]
    .map(result => {
      const score = fuzzyScore(result.name, inputValue);
      return score === null ? null : { result, score };
    })
    .filter((candidate): candidate is { result: SearchResult; score: number } => (
      candidate !== null
    ));

  return candidates
    .sort((left, right) => (
      right.score - left.score
      || TYPE_ORDER[left.result.type] - TYPE_ORDER[right.result.type]
      || left.result.name.localeCompare(right.result.name)
    ))
    .map(candidate => candidate.result);
};

const SEARCH_RESULT_PATHS = {
  OBJECTIVE: links.objective,
  PROJECT: links.project,
  TASK: links.task,
};

export const getSearchResultPath = (result: SearchResult) => (
  SEARCH_RESULT_PATHS[result.type](result.slug || result.id)
);

export const SEARCH_RESULT_LABEL_KEYS: Record<SearchResultType, 'objective' | 'project' | 'task'> = {
  OBJECTIVE: 'objective',
  PROJECT: 'project',
  TASK: 'task',
};
